import dayjs from "dayjs";

const getRelevantAnnotations = (annotationsRaw = [], minDate, filterPreset, assetId) =>
    annotationsRaw
        .filter(annotation => filterPreset === annotation.category_id || !annotation.category_id)
        .filter(annotation => assetId === annotation.asset_id || !annotation.asset_id)
        .filter(annotation => dayjs(annotation.date) >= dayjs(minDate) && dayjs(annotation.date) <= dayjs())

const TX_COLORS = {
    Buy: '#60ec7c',
    Sale: '#bc8aef',
    Dividend: '#ffd700',
    Swap: '#bababa',
    Burn: '#f76801',
}

const getTxAssetLabel = (assetCodeFrom, assetCode) =>
    assetCodeFrom ? `${assetCodeFrom} → ${assetCode}` : assetCode

const getClosestIndex = (labels, date) =>
    labels.reduce((best, label, i) => {
        const diff = Math.abs(new Date(label) - new Date(date))
        return diff < best.diff ? {i, diff} : best
    }, {i: 0, diff: Infinity}).i

const getTxPointAnnotation = (xValue, yValue) => ({
    type: 'point',
    xValue,
    yValue,
    radius: 5,
    backgroundColor: 'transparent',
    borderColor: 'white',
    borderWidth: 2,
})

const PROFIT_NEUTRAL_TYPES = new Set(['Burn', 'Dividend'])

const getTxHeaderColor = (type, isProfit) => {
    if (PROFIT_NEUTRAL_TYPES.has(type)) return TX_COLORS[type] ?? '#ffffff'
    return isProfit ? '#60ec7c' : '#bc8aef'
}

const getTxLabelAnnotation = (xValue, yValue, type, assetLabel, isNearTop, isProfit) => ({
    type: 'label',
    xValue,
    yValue,
    yAdjust: isNearTop ? 28 : -28,
    content: [type, assetLabel],
    backgroundColor: 'rgba(0,0,0,75%)',
    color: [getTxHeaderColor(type, isProfit), 'white'],
    padding: 4,
    font: [{size: 12}, {size: 13, weight: 'bold'}],
})

export const getAnnotations = ({
                                   labels,
                                   values,
                                   palette,
                                   dateFilter,
                                   assetId,
                                   filterPreset,
                                   annotationsRaw,
                                   transactionHoverDate,
                                   transactionHoverType,
                                   transactionHoverAssetCode,
                                   transactionHoverAssetCodeFrom,
                                   transactionHoverIsProfit,
                                   chartTransactions = [],
                               }) => {

    const isAnnotationSetReduced = labels.length > 400

    const {
        annotationGradient = 'rgba(255,255,255, 15%)',
    } = palette

    const labelAnnotations = getAnnotationsFromLabels(labels, isAnnotationSetReduced)

    const minDate = labels[labels.length - 1]

    const annotations = isAnnotationSetReduced ?
        labelAnnotations :
        [
            ...labelAnnotations,
            ...getRelevantAnnotations(annotationsRaw, minDate, filterPreset, assetId),
        ]

    let annotationOptions = annotations.reduce((acc, cur) => ({
        ...acc,
        [cur.date]: {
            type: 'line',
            xMin: cur.date,
            xMax: cur.date,
            borderColor: annotationGradient,
            borderWidth: 2,
            // borderDash: [15,5],
            label: {
                content: cur.text,
                display: true,
                position: 'end',
                backgroundColor: 'rgba(0,0,0,75%)',
            }
        }
    }), {})

    // annotationOptions.test = {
    //     type: 'label',
    //     // xValue: 2.5,
    //     xValue: '2024-12-06',
    //     xAdjust: 290,
    //     yAdjust: -100,
    //     backgroundColor: 'rgba(245,245,245)',
    //     content: ['This is my text', 'This is my text, second line'],
    //     textAlign: 'start',
    //     font: {
    //         size: 18
    //     },
    //     callout: {
    //         display: true,
    //         side: 10
    //     }
    // }

    if (dateFilter) {
        // overwrite other annotations
        annotationOptions = {
            dateFilter: {
                type: 'line',
                xMin: dateFilter,
                xMax: dateFilter,
                borderColor: annotationGradient,
                borderWidth: 2,
                // borderDash: [15,5],
                label: {
                    content: dateFilter,
                    display: true,
                    position: 'end',
                    backgroundColor: 'rgba(0,0,0,75%)',
                }
            }
        }
    }

    const oldestLabel = labels[labels.length - 1]
    const newestLabel = labels[0]
    const maxValue = Math.max(...values)
    const minValue = Math.min(...values)
    const getIsNearTop = yValue => (yValue - minValue) / (maxValue - minValue) > 0.8

    if (transactionHoverDate && transactionHoverDate >= oldestLabel && transactionHoverDate <= newestLabel) {
        const closestIndex = getClosestIndex(labels, transactionHoverDate)
        const yValue = values[closestIndex]
        const xValue = labels[closestIndex]
        annotationOptions = {
            ...annotationOptions,
            transactionHover: getTxPointAnnotation(xValue, yValue),
            transactionHoverLabel: getTxLabelAnnotation(
                xValue, yValue,
                transactionHoverType,
                getTxAssetLabel(transactionHoverAssetCodeFrom, transactionHoverAssetCode),
                getIsNearTop(yValue),
                transactionHoverIsProfit,
            ),
        }
    } else if (chartTransactions.length > 0) {
        chartTransactions.forEach(tx => {
            const closestIndex = getClosestIndex(labels, tx.date)
            const yValue = values[closestIndex]
            const xValue = labels[closestIndex]
            const assetLabel = getTxAssetLabel(tx.assetCodeFrom, tx.assetCode)
            annotationOptions[`tx_${tx.id}_point`] = getTxPointAnnotation(xValue, yValue)
            annotationOptions[`tx_${tx.id}_label`] = getTxLabelAnnotation(xValue, yValue, tx.type, assetLabel, getIsNearTop(yValue), tx.isProfit)
        })
    }

    return annotationOptions

}

const getAnnotationsFromLabels = (labels, isAnnotationSetReduced) => {
    const months = [
        "jan",
        "feb",
        "mar",
        "apr",
        "mayu",
        "jun",
        "jul",
        "aug",
        "sep",
        "oct",
        "nov",
        "dec",
    ]
    let yearPrev = 0
    return labels.map(label => {
        const [y, m, d] = label.split('-')
        if (yearPrev !== y && yearPrev !== 0) {
            yearPrev = y
            return {
                date: label,
                text: parseInt(yearPrev) + 1,
            }
        }
        yearPrev = y
        if (d === '01' && !isAnnotationSetReduced && false) {
            return {
                date: label,
                text: months[parseInt(m) - 1],
            }
        }
        return null
    }).filter(i => i)
}